import { Phone, MessageSquare, Calendar, Check, HeartHandshake, ShieldCheck, Clock, AlertTriangle, ArrowRight, ShieldAlert } from 'lucide-react';
import { siteConfig } from '../data';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function Hero() {
  return (
    <section className="relative min-h-[600px] lg:min-h-[800px] flex items-center overflow-hidden bg-slate-900">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover opacity-50"
        >
          <source src="/video/shutterstock_3693597661.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/95 via-slate-900/80 to-slate-900/95 lg:bg-gradient-to-r lg:from-slate-900/90 lg:via-slate-900/60 lg:to-slate-900/80" />
      </div>

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Main Text & CTAs */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 max-w-3xl"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="flex items-center text-white/90 font-medium text-sm">
                <Check className="w-5 h-5 text-blue-400 mr-2 shrink-0" />
                Hjælp via telefon, fjernsupport og hjemmebesøg
              </div>
              <div className="flex items-center text-white/90 font-medium text-sm">
                <Check className="w-5 h-5 text-blue-400 mr-2 shrink-0" />
                Vi kører ud fra Slagelse
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              Tryg hjælp til ældre og sårbare borgere på nettet
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-10 leading-relaxed">
              Vi hjælper med at beskytte mod online svindel, kontaktbedrageri og usikkerhed på nettet — med personlig og tålmodig hjælp.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white text-lg font-bold hover:bg-blue-700 hover:-translate-y-0.5 transition-all shadow-lg"
              >
                <Phone className="w-5 h-5 mr-3" />
                Ring nu
              </a>
              <Link
                to="/#kontakt"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 text-lg font-bold hover:bg-slate-100 hover:-translate-y-0.5 transition-all shadow-lg"
              >
                <Calendar className="w-5 h-5 mr-3" />
                Book samtale
              </Link>
              <a
                href={`sms:${siteConfig.sms.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white text-lg font-bold border-2 border-white hover:-translate-y-0.5 transition-all"
              >
                <MessageSquare className="w-5 h-5 mr-3" />
                Send SMS
              </a>
            </div>
          </motion.div>

          {/* Right Column: Velkomst & Første samtale boks */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5 w-full"
          >
            <div className="bg-white rounded-none p-6 sm:p-8 shadow-2xl border-t-4 border-[#103B66] relative overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-[#103B66] bg-blue-50 px-2.5 py-1">
                  Kom godt i gang
                </span>
                <span className="text-2xl font-black text-[#103B66]">Kun 99,-</span>
              </div>

              <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 leading-tight mb-2">
                Første tryghedssamtale <span className="block text-base sm:text-lg font-bold text-slate-700 mt-1">(ca. 10-15 minutter)</span>
              </h2>

              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                Få ro i maven med en personlig gennemgang over telefonen. Vi rådgiver dig tålmodigt om dine konkrete spørgsmål og sikring mod svindel.
              </p>

              <div className="bg-slate-50 border border-slate-200 p-4 mb-5 text-xs text-slate-700 space-y-2">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Vi opretter dig i systemet, så vi kan se din historik hvis du ringer igen</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Ingen teknisk snak – vi taler i et roligt og forståeligt sprog</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Mulighed for efterfølgende hjemmebesøg i Slagelse og omegn</span>
                </div>
              </div>

              <a
                href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
                className="w-full inline-flex items-center justify-center px-4 py-3.5 bg-[#103B66] hover:bg-[#0c2e50] text-white font-bold transition-all shadow text-sm mb-3"
              >
                <Phone className="w-4 h-4 mr-2" />
                Ring og book nu ({siteConfig.phone})
              </a>

              <div className="text-center">
                <a
                  href="#seneste-nyt"
                  className="inline-flex items-center justify-center text-xs text-red-600 hover:text-red-700 font-semibold gap-1"
                >
                  <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                  <span>Se seneste nyt om det store datalæk i bunden af siden</span>
                  <ArrowRight className="w-3 h-3" />
                </a>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

